import type { HTMLAttributes } from 'react';

import { cn } from '@shared/lib/cn';

import {
  PROGRESS_BAR_MAX,
  PROGRESS_BAR_MIN,
  type ProgressBarStatus,
} from './progress-bar.constant';
import { ProgressBarSkeleton } from './progress-bar.skeleton.ui';

export interface ProgressBarProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'children' | 'role'
> {
  /** 채움 비율(0~100). 범위를 벗어나면 잘라 낸다. 기본 0 */
  value?: number;
  /** default · init(스켈레톤). 기본 default */
  status?: ProgressBarStatus;
}

function clamp(value: number): number {
  if (Number.isNaN(value)) return PROGRESS_BAR_MIN;
  return Math.min(PROGRESS_BAR_MAX, Math.max(PROGRESS_BAR_MIN, value));
}

/**
 * 진행 막대. 옅은 회색 트랙(높이 12 · 모서리 6) + primary 채움. 채움은 트랙에 잘려 왼쪽 끝만 둥글다.
 * 너비는 부모를 채운다(Figma 의 240 은 예시 너비). 이름은 `aria-label` · `aria-labelledby` 로 준다.
 */
export function ProgressBar({
  value = 0,
  status = 'default',
  className,
  ...rest
}: ProgressBarProps) {
  if (status === 'init') return <ProgressBarSkeleton className={className} />;

  const percent = clamp(value);
  return (
    <div
      role="progressbar"
      aria-valuemin={PROGRESS_BAR_MIN}
      aria-valuemax={PROGRESS_BAR_MAX}
      aria-valuenow={percent}
      data-status={status}
      {...rest}
      className={cn('h-3 w-full overflow-hidden rounded-md bg-bg-muted', className)}
    >
      {/* 채움 너비는 값에서 계산한다(토큰이 아닌 자료 값이라 인라인 style) */}
      <div
        data-part="fill"
        className="h-full bg-bg-primary transition-all duration-(--motion-duration-base)"
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}
